import { createSlice, createSelector } from '@reduxjs/toolkit';
import axios from '../../services/authService';
import { API_ENDPOINTS } from '../../config/apiConfig';

export const getDevicesSlice = createSlice({
  name: 'getDevices',
  initialState: {
    devices: [],
    loading: false,
    error: null,
  },
  reducers: {
    reset: (state) => {
      state.devices = [];
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setDevices: (state, action) => {
      state.devices = action.payload;
      state.loading = false;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const { reset, setLoading, setDevices, setError } = getDevicesSlice.actions;

/**
 * Fetch all devices from the database
 */
export const getDevicesAsync = () => async (dispatch) => {
  dispatch(reset());
  dispatch(setLoading(true));

  try {
    const response = await axios.get(API_ENDPOINTS.GET_DEVICES);

    // Parse response data
    let devices = [];
    if (response.data?.data && Array.isArray(response.data.data)) {
      devices = response.data.data;
    } else if (response.data?.body) {
      // Fallback for old response format
      const parsedBody =
        typeof response.data.body === 'string'
          ? JSON.parse(response.data.body)
          : response.data.body;
      devices = parsedBody.resDataObject?.devices || parsedBody.devices || [];
    }

    // Filter out soft-deleted devices (where deleted_at is not null)
    devices = devices.filter((device) => !device.deleted_at);

    dispatch(setDevices(devices));
    return { success: true, data: devices };
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch devices';
    console.error('Error fetching devices:', errorMessage);
    dispatch(setError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Basic Selectors
export const selectDevices = (state) => state.getDevices.devices;
export const selectDevicesLoading = (state) => state.getDevices.loading;
export const selectDevicesError = (state) => state.getDevices.error;

// Market Filtering Selector (by market database id)
export const selectDevicesByMarket = createSelector(
  [selectDevices, (state, marketId) => marketId],
  (devices, marketId) => {
    if (marketId === null || marketId === undefined || marketId === '') {
      // If no market is selected, return empty array (matching FlowList behavior)
      return [];
    }

    const id = Number(marketId);
    // Filter devices by market_id
    return devices.filter((device) => Number(device.market_id) === id);
  },
);

// Helper function to get filtered devices based on current market
export const getDevicesForCurrentMarket = (state) => {
  // Get current market from Redux state or localStorage
  const reduxMarket = state.marketSelection?.value;
  const localStorageMarket = localStorage.getItem('market')
    ? JSON.parse(localStorage.getItem('market'))
    : null;
  const currentMarket = reduxMarket || localStorageMarket;

  // Return empty array if no market (matching FlowList behavior)
  if (!currentMarket?.id) {
    return [];
  }

  return selectDevicesByMarket(state, currentMarket.id);
};

export default getDevicesSlice.reducer;
